import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getImageUrl } from '../services/imageService';

const Header: React.FC = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    
    const isActive = (path: string) => location.pathname === path;
    
    // Fecha o menu ao mudar de página
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    // Fecha o menu ao pressionar ESC
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsMenuOpen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Previne scroll quando menu está aberto
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    const menuItems = [
        { path: '/', label: 'Início', icon: 'fa-home' },
        { path: '/students', label: 'Alunos', icon: 'fa-user-graduate' },
        { path: '/moments', label: 'Momentos', icon: 'fa-clock' },
        { path: '/gallery', label: 'Galeria', icon: 'fa-images' },
        { path: '/testimonials', label: 'Depoimentos', icon: 'fa-comments' },
    ];
    
    return (
        <>
            <header 
                className="bg-black text-gold py-4 px-4 md:py-6 md:px-6 shadow-md sticky top-0 z-50"
                role="banner"
            >
                <div className="flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex items-center space-x-2 md:space-x-4">
                        <img
                            src={getImageUrl('root', 'Logo.png')}
                            alt="Logo do Anuário Digital"
                            className="w-12 h-12 md:w-16 md:h-16 object-contain"
                        />
                        <Link to="/" className="flex items-center no-underline hover:no-underline">
                            <h1
                                className="text-xl md:text-3xl font-bold tracking-wide hover:text-yellow-400 transition-all text-gold"
                                style={{
                                    fontFamily: "'Cinzel', serif",
                                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                                }}
                            >
                                Anuário Digital
                            </h1>
                        </Link>
                    </div>

                    {/* Botão Hamburger - Mobile */}
                    <button
                        className="md:hidden flex flex-col justify-center items-center w-12 h-12 rounded-lg bg-gold focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                    >
                        <span 
                            className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}
                        />
                        <span 
                            className={`block w-6 h-0.5 bg-black mt-1.5 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}
                        />
                        <span 
                            className={`block w-6 h-0.5 bg-black mt-1.5 transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}
                        />
                    </button>

                    {/* Navegação Desktop */}
                    <nav aria-label="Navegação principal" className="hidden md:block">
                        <ul className="flex space-x-2 lg:space-x-4">
                            {menuItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={`flex items-center space-x-2 px-3 py-2 lg:px-4 bg-gold text-black rounded-full text-sm lg:text-base font-semibold hover:bg-yellow-400 transition-all no-underline hover:no-underline focus-visible:ring-2 focus-visible:ring-white ${isActive(item.path) ? 'ring-2 ring-white' : ''}`}
                                        aria-current={isActive(item.path) ? 'page' : undefined}
                                    >
                                        <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                                        <span>{item.label}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                {/* Menu Mobile - Overlay */}
                <div 
                    className={`fixed inset-0 bg-black/80 z-40 md:hidden transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={() => setIsMenuOpen(false)}
                    aria-hidden="true"
                />

                {/* Menu Mobile - Drawer */}
                <nav
                    id="mobile-menu"
                    className={`fixed top-0 right-0 h-full w-72 bg-black border-l-2 border-gold z-50 transform transition-transform duration-300 ease-in-out md:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                    aria-label="Menu de navegação mobile"
                    aria-hidden={!isMenuOpen}
                >
                    {/* Header do menu mobile */}
                    <div className="flex items-center justify-between p-4 border-b border-gold/30">
                        <span className="text-gold font-bold text-lg">Menu</span>
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-gold text-black hover:bg-yellow-400 transition-colors"
                            aria-label="Fechar menu"
                        >
                            <i className="fas fa-times text-xl" aria-hidden="true"></i>
                        </button>
                    </div>

                    {/* Links do menu */}
                    <ul className="flex flex-col p-4 space-y-3">
                        {menuItems.map((item) => (
                            <li key={item.path}>
                                <Link
                                    to={item.path}
                                    className={`flex items-center space-x-3 px-4 py-3 bg-gold text-black rounded-xl font-semibold hover:bg-yellow-400 transition-all no-underline hover:no-underline ${isActive(item.path) ? 'ring-2 ring-white' : ''}`}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                    tabIndex={isMenuOpen ? 0 : -1}
                                >
                                    <i className={`fas ${item.icon} text-lg w-6`} aria-hidden="true"></i>
                                    <span className="text-base">{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Footer do menu mobile */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gold/30">
                        <p className="text-gold/60 text-sm text-center">
                            Turma AMS 2025
                        </p>
                    </div>
                </nav>
            </header>
        </>
    );
};

export default Header;